import { Component, useEffect, useRef, useState } from 'react';
import Globe from 'react-globe.gl';
import dummyRoutes from '../../data/dummyRoutes.json';
import styles from './style.module.scss';

class GlobeErrorBoundary extends Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError() {
		return { hasError: true };
	}

	componentDidCatch(error) {
		if (this.props.onError) {
			this.props.onError(error);
		}
	}

	render() {
		if (this.state.hasError) return this.props.fallback;
		return this.props.children;
	}
}

export default function Earth() {
	const globeEl = useRef();
	const [airports, setAirports] = useState([]);

	const COUNTRY = 'United States';
	const OPACITY = 0.44;

	const airportParse = ([
		airportId,
		name,
		city,
		country,
		iata,
		icao,
		lat,
		lng,
		alt,
		timezone,
		dst,
		tz,
		type,
		source,
	]) => ({
		airportId,
		name,
		city,
		country,
		iata,
		icao,
		lat,
		lng,
		alt,
		timezone,
		dst,
		tz,
		type,
		source,
	});
	const routeParse = ([
		airline,
		airlineId,
		srcIata,
		srcAirportId,
		dstIata,
		dstAirportId,
		codeshare,
		stops,
		equipment,
	]) => ({
		airline,
		airlineId,
		srcIata,
		srcAirportId,
		dstIata,
		dstAirportId,
		codeshare,
		stops,
		equipment,
	});

	const parseCSVRows = (csvString, parser) => {
		// Split the CSV string into rows
		const rows = csvString.trim().split('\n');
		// Map each row to an array of values, then use the parser function on each
		return rows.map((row) => {
			// Split each row by comma, considering CSV-specific edge cases if necessary
			const values = row.split(',').map((value) => value.trim());
			// Use the provided parser function to convert array of values to an object
			return parser(values);
		});
	};

	useEffect(() => {
		let rafId;
		const initializeGlobe = () => {
			if (!globeEl.current) {
				rafId = requestAnimationFrame(initializeGlobe);
				return;
			}

			globeEl.current.pointOfView({ lat: 39.6, lng: -98.5, altitude: 2 });
			const controls = globeEl.current.controls();
			controls.enabled = true;
			controls.autoRotate = true;
			controls.autoRotateSpeed = 0.6;
			controls.enableRotate = true;
			controls.enableZoom = false;
		};

		initializeGlobe();

		// Assuming you still want to fetch airports data
		fetch(
			'https://raw.githubusercontent.com/jpatokal/openflights/master/data/airports.dat'
		)
			.then((res) => res.text())
			.then((csvString) => {
				const parsedData = parseCSVRows(csvString, airportParse);

				const usAirports = parsedData.filter(
					(airport) => airport.country === '"United States"'
				);

				setAirports(usAirports);
				// console.log(parsedData);
			});

		return () => {
			if (rafId) cancelAnimationFrame(rafId);
		};
	}, []);

	return (
		<>
			{/* <script src="//unpkg.com/d3-dsv"></script>
			<script src="//unpkg.com/index-array-by"></script> */}
			<div className={styles.earth_container}>
				<GlobeErrorBoundary
					fallback={
						<div className={styles.fallback}>
							<div className={styles.fallbackGlobe} aria-hidden="true">
								<div className={styles.fallbackGlow}></div>
							</div>
							<p className={styles.fallbackText}>
								Interactive 3D globe is unavailable on this device.
							</p>
						</div>
					}
				>
					<Globe
						ref={globeEl}
						enablePointerInteraction={true}
						globeImageUrl="//unpkg.com/three-globe/example/img/earth-night.jpg"
						arcsData={dummyRoutes}
						arcLabel={(d) =>
							`${d.airline}: ${d.srcIata} &#8594; ${d.dstIata}`
						}
						arcStartLat={(d) => +d.srcAirport.lat}
						arcStartLng={(d) => +d.srcAirport.lng}
						arcEndLat={(d) => +d.dstAirport.lat}
						arcEndLng={(d) => +d.dstAirport.lng}
						arcDashLength={0.8}
						arcDashGap={0.4}
						arcDashInitialGap={() => Math.random()}
						arcDashAnimateTime={3000}
						arcStroke={0.45}
						arcAltitude={0.2}
						arcColor={(d) => [
							`rgba(155, 99, 241, ${OPACITY})`,
							`rgba(233, 24, 75, ${OPACITY})`,
							`rgba(77, 77, 255, ${OPACITY})`,
							`rgba(232, 235, 255, ${OPACITY})`,
							`rgba(255, 155, 251, ${OPACITY})`,
						]}
						arcsTransitionDuration={0}
						pointsData={airports}
						pointColor={() => 'orange'}
						pointAltitude={0.01}
						// pointRadius={0.02}
						pointsMerge={true}
					/>
				</GlobeErrorBoundary>
			</div>
		</>
	);
}
